import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ReceiptText, MapPin, User, MessageCircle, LogOut, ChevronRight, ArrowUpRight, X } from "lucide-react";
import { useWaUrl } from "../lib/waUrl";
import { useChat } from "../chat/chatKonteks";

export const inisialNama = (nama) => {
  const kata = String(nama || "").trim().split(/\s+/).filter(Boolean);
  if (!kata.length) return "?";
  return (kata.length === 1 ? kata[0][0] : kata[0][0] + kata[kata.length - 1][0]).toUpperCase();
};

const Avatar = ({ nama, size = 48 }) => (
  <span
    className="shrink-0 inline-flex items-center justify-center rounded-full bg-[#1D1D1F] text-white font-bold"
    style={{ width: size, height: size, fontSize: size * 0.36 }}
    data-testid="akun-avatar"
  >
    {inisialNama(nama)}
  </span>
);

export const AvatarInisial = Avatar;

const KotakStatus = ({ label, jumlah, onClick, tid }) => (
  <button type="button" onClick={onClick} data-testid={tid}
    className="flex-1 rounded-[4px] bg-[#F5F5F7] px-2 py-3 text-center transition-colors hover:bg-[#E5E5EA]">
    <span className={`block text-[22px] font-bold leading-none ${jumlah > 0 ? "text-[#1D1D1F]" : "text-[#6E6E73]"}`}>
      {jumlah}
    </span>
    <span className="mt-1 block text-[11px] text-[#6E6E73]">{label}</span>
  </button>
);

const BarisMenu = ({ icon: Icon, label, onClick, href, tid, garis }) => {
  const isi = (
    <>
      <Icon size={18} strokeWidth={1.6} className="shrink-0 text-[#1D1D1F]" />
      <span className="flex-1 text-sm text-[#1D1D1F]">{label}</span>
      {href
        ? <ArrowUpRight size={16} strokeWidth={1.6} className="shrink-0 text-[#6E6E73]" />
        : <ChevronRight size={16} strokeWidth={1.6} className="shrink-0 text-[#6E6E73]" />}
    </>
  );
  const cls = `flex w-full items-center gap-3 px-4 h-[50px] text-left transition-colors hover:bg-[#F5F5F7] ${garis ? "border-b border-[#E5E5EA]" : ""}`;
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={tid}>{isi}</a>
  ) : (
    <button type="button" onClick={onClick} className={cls} data-testid={tid}>{isi}</button>
  );
};

/**
 * Panel akun: dropdown di desktop, bottom sheet di HP.
 * Isi sama persis untuk keduanya (board "Popup akun").
 */
export default function AccountMenu({ user, counts, onClose, onOpenProfile, onOpenAddress, onLogout, variant = "desktop" }) {
  const nav = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest("[data-akun-trigger]")) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [onClose]);

  const waUrl = useWaUrl();
  const chat = useChat();
  const keTab = (tab) => { onClose(); nav(`/pesanan?tab=${tab}`); };
  const sub = [user?.nama_usaha, user?.email].filter(Boolean).join(" · ");

  const status = (
    <div className="flex gap-2 px-4 pb-4" data-testid="akun-status-boxes">
      <KotakStatus label="Belum dibayar" jumlah={counts.belum} tid="akun-status-belum" onClick={() => keTab("MENUNGGU_BAYAR")} />
      <KotakStatus label="Diproses" jumlah={counts.diproses} tid="akun-status-diproses" onClick={() => keTab("DIPROSES")} />
      <KotakStatus label="Dikirim" jumlah={counts.dikirim} tid="akun-status-dikirim" onClick={() => keTab("DIKIRIM")} />
    </div>
  );

  const menu = (garis) => (
    <>
      <BarisMenu icon={ReceiptText} label="Pesanan saya" tid="akun-menu-pesanan" garis={garis}
        onClick={() => { onClose(); nav("/pesanan"); }} />
      <BarisMenu icon={MapPin} label="Alamat pengiriman" tid="akun-menu-alamat" garis={garis}
        onClick={() => { onClose(); onOpenAddress(); }} />
      <BarisMenu icon={User} label="Profil & kata sandi" tid="akun-menu-profil" garis={garis}
        onClick={() => { onClose(); onOpenProfile(); }} />
      {chat.aktif ? (
        <BarisMenu icon={MessageCircle} label="Chat dengan Hoonstore" tid="akun-menu-whatsapp" garis={garis} onClick={() => { onClose(); chat.buka(null); }} />
      ) : waUrl && (
        <BarisMenu icon={MessageCircle} label="Bantuan via WhatsApp" tid="akun-menu-whatsapp" href={waUrl} />
      )}
    </>
  );

  if (variant === "mobile") {
    return (
      <div className="md:hidden fixed inset-0 z-[60] flex items-end bg-black/40" data-testid="akun-sheet-overlay">
        <div ref={ref} className="w-full rounded-t-2xl bg-white pb-5 shadow-2xl" data-testid="akun-sheet">
          <div className="flex justify-center pt-2.5 pb-1">
            <span className="h-1 w-10 rounded-full bg-[#E5E5EA]" />
          </div>
          <div className="flex items-center gap-3 px-4 pb-4 pt-2">
            <Avatar nama={user?.nama} size={48} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-bold text-[#1D1D1F]" data-testid="akun-nama">{user?.nama || "Akun"}</div>
              <div className="truncate text-xs text-[#6E6E73]" data-testid="akun-sub">{sub}</div>
            </div>
            <button type="button" onClick={onClose} aria-label="Tutup" data-testid="akun-sheet-close"
              className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#F5F5F7] text-[#1D1D1F]">
              <X size={18} strokeWidth={1.8} />
            </button>
          </div>
          {status}
          <div className="border-t border-[#E5E5EA]">{menu(true)}</div>
          <div className="px-4 pt-4">
            <button type="button" onClick={onLogout} data-testid="akun-menu-keluar-m"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-[4px] border border-[#E5E5EA] text-sm font-semibold text-[#1D1D1F]">
              <LogOut size={18} strokeWidth={1.6} /> Keluar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} data-testid="akun-popup"
      className="absolute right-0 top-[calc(100%+6px)] z-50 w-[410px] overflow-hidden rounded-[8px] border border-[#E5E5EA] bg-white shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
      <div className="flex items-center gap-3 px-4 pb-4 pt-4">
        <Avatar nama={user?.nama} size={48} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-bold text-[#1D1D1F]" data-testid="akun-nama">{user?.nama || "Akun"}</div>
          <div className="truncate text-xs text-[#6E6E73]" data-testid="akun-sub">{sub}</div>
        </div>
      </div>
      {status}
      <div className="border-t border-[#E5E5EA] py-1">{menu(false)}</div>
      <div className="border-t border-[#E5E5EA] py-1">
        <button type="button" onClick={onLogout} data-testid="akun-menu-keluar"
          className="flex w-full items-center gap-3 px-4 h-[50px] text-left transition-colors hover:bg-[#F5F5F7]">
          <LogOut size={18} strokeWidth={1.6} className="shrink-0 text-[#1D1D1F]" />
          <span className="text-sm text-[#1D1D1F]">Keluar</span>
        </button>
      </div>
    </div>
  );
}
